import { useMemoizedFn } from 'ahooks';
import { useMemo } from 'react';
import { getMyNexusHistory } from '@/app/apis';
import { isEnterpriseDistribution } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { FormTabType, FreeCreditsTask, ModelVersion } from '@/app/types';
import { type FreeCreditsTaskName, NewbieTaskName, NormalFreeCreditsTaskName } from '../constants';
import { getRemainingTaskReward } from './free-credits-task-reward';
import { useCredits } from './use-credits';
import { useFetch } from './use-fetch';

// 新手任务的名称前缀或标识
export const NEWBIE_TASK_PREFIX = 'newbie_task_';

type TaskPriorityKey = Exclude<FreeCreditsTaskName, 'mission_watch_ad_credit'>;

// 任务排序配置
const taskPriority: Record<TaskPriorityKey, number> = {
  [NormalFreeCreditsTaskName.invitecodecatalyst_credit]: 10,
  [NewbieTaskName.newbie_task_i2v_v2_credit]: 20,
  [NewbieTaskName.newbie_task_i2v_Q1_credit]: 30,
  [NewbieTaskName.newbie_task_c2v_Q1_credit]: 40,
  [NewbieTaskName.newbie_task_template_credit]: 50,
  [NewbieTaskName.newbie_task_i2v_Q2_credit]: 20,
  [NewbieTaskName.newbie_task_c2v_Q2_credit]: 30,
  [NewbieTaskName.newbie_task_material_create_credit]: 40,
  [NewbieTaskName.newbie_task_use_material_credit]: 50,
  [NewbieTaskName.newbie_task_image_generate_credit]: 42,
  [NewbieTaskName.newbie_task_image_to_video_credit]: 52,
  [NormalFreeCreditsTaskName.download_app_credit]: 60,
  [NormalFreeCreditsTaskName.questionnaire_credit]: 70,
  [NormalFreeCreditsTaskName.every_day_credit]: 80,
  [NormalFreeCreditsTaskName.new_user_credit]: 90,
  [NormalFreeCreditsTaskName.subscribe_email_credit]: 100,
  [NormalFreeCreditsTaskName.renewal_questionnaire_credit]: 65,
};

// 任务排序函数
const sortTasks = (tasks: FreeCreditsTask[]): FreeCreditsTask[] => {
  // 使用 [...tasks] 创建一个新数组进行排序，避免直接修改原数组
  return [...tasks].sort((a, b) => {
    // 将任务名称断言为 TaskKey，以便在 taskPriority 中查找
    const aPriority = taskPriority[a.name as TaskPriorityKey] || 999;
    const bPriority = taskPriority[b.name as TaskPriorityKey] || 999;
    return aPriority - bPriority;
  });
};

// 将原始任务数据转换为增强的任务数据
const convertToEnhancedTask = (task: FreeCreditsTask): EnhancedNewbieTask => {
  const totalProgress = task.total_times ?? 1;

  return {
    ...task,
    totalReward: getRemainingTaskReward(task),
    eachReward: task.credits_award ?? 0,
    totalProgress,
    currentProgress: task.finished ? totalProgress : (task.finished_times ?? 0),
  };
};

// 增强的新手任务接口，包含配置信息
export interface EnhancedNewbieTask extends FreeCreditsTask {
  totalReward: number;
  eachReward: number;
  totalProgress: number;
  currentProgress: number;
  modelVersion?: ModelVersion;
  taskType?: FormTabType;
}

/**
 * 免费积分任务管理 Hook
 *
 * 功能：
 * 1. 获取和刷新任务列表
 * 2. 查询某个提交是否属于新手任务（用于埋点）
 * 3. 提供任务查询和状态管理功能
 * 4. 提供增强的任务数据（包含配置信息）
 */
export const useFreeCreditsTasks = () => {
  const { freeCreditsTasks, updateFreeCreditsTasks } = useStore((state) => ({
    freeCreditsTasks: state.freeCreditsTasks,
    updateFreeCreditsTasks: state.updateFreeCreditsTasks,
  }));

  const { updateCreditsAsync } = useCredits();

  // 使用 useFetch 管理 API 请求
  const {
    run: fetchFreeCreditTasksRaw,
    loading,
    error,
  } = useFetch(getMyNexusHistory, {
    manual: true,
    onSuccess: (response) => {
      const tasks = response?.data?.history || [];
      const sortedTasks = sortTasks(tasks as FreeCreditsTask[]);
      updateFreeCreditsTasks(sortedTasks);
      updateCreditsAsync();
      return sortedTasks;
    },
  });

  const fetchFreeCreditTasks = useMemoizedFn(() => {
    if (isEnterpriseDistribution) {
      updateFreeCreditsTasks([]);
      updateCreditsAsync();
      return;
    }

    fetchFreeCreditTasksRaw();
  });

  // 获取所有新手任务（包括已完成和未完成的）- 增强版本
  const [allNewbieTasks, unfinishedNewbieTasks] = useMemo(() => {
    const rawNewbieTasks = freeCreditsTasks.filter((task) => task.name?.startsWith(NEWBIE_TASK_PREFIX));

    // 转换为增强的任务数据
    const enhancedTasks = rawNewbieTasks.map(convertToEnhancedTask);

    return [enhancedTasks, enhancedTasks.filter((task) => task.totalReward > 0)];
  }, [freeCreditsTasks]);

  /**
   * 根据新手任务名称获取预计的新手任务奖励
   * @param newbieTaskName - 新手任务名称
   * @returns 预计的新手任务奖励
   */
  const getExpectedNewbieReward = useMemoizedFn((newbieTaskName: NewbieTaskName) => {
    return unfinishedNewbieTasks.find((task) => task.name === newbieTaskName)?.eachReward ?? 0;
  });

  return {
    allNewbieTasks,
    error,
    fetchFreeCreditTasks,
    freeCreditsTasks,
    loading,
    unfinishedNewbieTasks,
    getExpectedNewbieReward,
  };
};
